/**
 * Google Maps URLs used across the app.
 *
 * - **Coordinates** — ``maps.google.com/?q=lat,long&t=k`` (ChargePoint / Blink station detail).
 * - **Address search** — ``google.com/maps/search/?api=1&query=…`` (HITL / utility bills).
 */

/** Same format as ChargePoint / Blink station detail links. */
export function googleMapsUrlFromCoordinates(
  latitude: number | string,
  longitude: number | string
): string {
  return `https://maps.google.com/?q=${latitude},${longitude}&t=k`;
}

export function googleMapsSearchUrlFromQuery(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export function googleMapsQueryFromLines(lines: readonly string[]): string | null {
  const q = lines
    .map(l => l.trim())
    .filter(Boolean)
    .join(', ');
  return q.length > 0 ? q : null;
}

export function googleMapsSearchUrlFromLines(lines: readonly string[]): string | null {
  const q = googleMapsQueryFromLines(lines);
  return q ? googleMapsSearchUrlFromQuery(q) : null;
}

export function linesFromSitePhysicalAddress(site: {
  address_line_1?: string | null;
  address_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
}): string[] {
  const out: string[] = [];
  const l1 = site.address_line_1?.trim();
  const l2 = site.address_line_2?.trim();
  const city = site.city?.trim();
  const state = site.state?.trim();
  const zip = site.postal_code?.trim();
  const country = site.country?.trim().toUpperCase();
  if (l1) {
    out.push(l1);
  }
  if (l2) {
    out.push(l2);
  }
  const cityLine = [city, state].filter(Boolean).join(', ').trim();
  const cityZip = [cityLine, zip].filter(Boolean).join(' ').trim();
  if (cityZip) {
    out.push(cityZip);
  }
  if (country && country !== 'US') {
    out.push(country);
  }
  return out;
}

export function googleMapsSearchUrlFromSite(site: {
  address_line_1?: string | null;
  address_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
}): string | null {
  return googleMapsSearchUrlFromLines(linesFromSitePhysicalAddress(site));
}
